// Agrega a Satisfação (CSAT) ao nível do grupo para o Dashboard. O backend só expõe
// analytics por hotel (`GET /csat/config/analytics?hotel_id=`, gated por require_csat_manage
// -> licença CSAT por hotel), tal como o resto do dashboard já faz para outros dados
// (ver dashboardMetrics.js) — por isso agregamos aqui, no cliente, por hotel do grupo.

const EMPTY_DISTRIBUTION = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };

function mergeDistributions(list) {
  const merged = { ...EMPTY_DISTRIBUTION };
  list.forEach((d) => {
    Object.keys(merged).forEach((k) => {
      merged[k] += Number(d?.[k] || 0);
    });
  });
  return merged;
}

function mergeTimeSeries(lists) {
  const byDate = new Map();
  lists.forEach((series) => {
    (series || []).forEach((point) => {
      const prev = byDate.get(point.date) || { sum: 0, total: 0 };
      byDate.set(point.date, {
        sum: prev.sum + (point.average || 0) * point.total,
        total: prev.total + point.total,
      });
    });
  });
  return Array.from(byDate.entries())
    .map(([date, { sum, total }]) => ({
      date,
      total,
      average: total ? Math.round((sum / total) * 100) / 100 : null,
    }))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
}

export async function loadCsatSummary(axiosAuth, groupId, dateFrom, dateTo, hotelId) {
  const { data: allHotels } = await axiosAuth
    .get(`/hotels/group/${groupId}`)
    .catch(() => ({ data: [] }));
  const hotels = hotelId
    ? (allHotels || []).filter((h) => String(h.id) === String(hotelId))
    : allHotels;

  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  const results = await Promise.allSettled(
    (hotels || []).map((h) =>
      axiosAuth
        .get('/csat/config/analytics', { params: { ...params, hotel_id: h.id } })
        .then(({ data }) => ({ hotel: h, data })),
    ),
  );

  const perHotel = results
    .filter((r) => r.status === 'fulfilled')
    .map((r) => r.value);

  if (perHotel.length === 0) {
    return { available: false };
  }

  const total = perHotel.reduce((acc, { data }) => acc + data.total, 0);
  const distribution = mergeDistributions(perHotel.map(({ data }) => data.distribution));
  const sumRatings = Object.entries(distribution).reduce(
    (acc, [rating, count]) => acc + Number(rating) * count,
    0,
  );
  const average = total ? Math.round((sumRatings / total) * 100) / 100 : null;
  const percentages = Object.fromEntries(
    Object.entries(distribution).map(([k, v]) => [
      k,
      total ? Math.round((v / total) * 1000) / 10 : 0,
    ]),
  );
  const positive = distribution['4'] + distribution['5'];
  const neutral = distribution['3'];
  const negative = distribution['1'] + distribution['2'];

  const negativeRatings = perHotel
    .flatMap(({ hotel, data }) =>
      (data.negative_ratings || []).map((r) => ({ ...r, hotel_name: hotel.name })),
    )
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .slice(0, 50);

  return {
    available: true,
    total,
    average,
    distribution,
    percentages,
    positive_rate: total ? Math.round((positive / total) * 1000) / 10 : null,
    neutral_rate: total ? Math.round((neutral / total) * 1000) / 10 : null,
    negative_rate: total ? Math.round((negative / total) * 1000) / 10 : null,
    time_series: mergeTimeSeries(perHotel.map(({ data }) => data.time_series)),
    negative_ratings: negativeRatings,
  };
}
